/**
 * The content of a WL-3 end-of-job service report (fieldiq-api#1339), as the
 * API stored it: a draft's last built copy, or an issued report's frozen one.
 *
 * EVERY FIGURE IS THE API'S. Depths and footage arrive as `{ value, unit }`
 * already in the tenant's units (lib/wl-report.ts `depthOf`), hours and totals
 * already summed. This file only lays them out; it converts, sums and derives
 * nothing. An absent figure reads `—`, never 0.
 *
 * Only the office-approved day sheets are in the content; the ones that are
 * not are counted and named by the API's warning, shown on the report page.
 *
 * Panels stack full width (no side-by-side panels). A section with no rows is
 * omitted, except Runs and Plugs left in hole, whose emptiness is itself a
 * fact a reader looks for.
 */
import React from 'react';
import { formatInstantTime } from '../../lib/calendar-date';
import { pcTestKindLabel, PC_RESULT_LABEL } from '../../lib/pressure-control';
import { fmtWlNumber } from '../../lib/wireline-slickline';
import { wlClockRange, wlDay, wlHours } from '../../lib/wireline-slickline-jobs';
import { wlReportInterval, wlReportQty } from '../../lib/wireline-slickline-reports';
import type { WlReportContent } from '../../types/wireline-slickline-reports';
import { WlPanel, WlSpec } from './WlBits';

/**
 * The PDF prints this line under its plugs table (lib/wl-report.ts); the JSON
 * does not carry it, so it is repeated here in the API's words.
 */
export const WL_PLUGS_CAVEAT = "As this job's record shows: plugs set on an earlier job are not listed here.";

const n = (v: number | null | undefined) => (v === null || v === undefined ? '—' : fmtWlNumber(v));

function ReportTable({ head, rows, testId, minWidth = 'min-w-[640px]' }: { head: string[]; rows: React.ReactNode[][]; testId?: string; minWidth?: string }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className={`w-full ${minWidth} text-sm`}>
        <thead>
          <tr className="text-left text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            {head.map((h) => (
              <th key={h} className="whitespace-nowrap px-4 py-2">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-fieldiq-border">
          {rows.map((r, i) => (
            <tr key={i} data-testid={testId ? `${testId}-row` : undefined}>
              {r.map((c, j) => (
                <td key={j} className="whitespace-nowrap px-4 py-2 font-mono text-fieldiq-text">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** "3 Nov 2026 · WL-07 · Run 2" — which run a child row belongs to. */
const runRef = (r: { workDate: string; unitNumber: string | null; runNumber: number }) =>
  `${wlDay(r.workDate)} · ${r.unitNumber || '—'} · Run ${r.runNumber}`;

export function WlReportContentView({ content }: { content: WlReportContent }) {
  const t = content.totals;
  const zone = content.timeZone;
  return (
    <div className="space-y-4" data-testid="wl-report-content">
      <WlPanel eyebrow="Totals" title="Summary" testId="wl-report-totals">
        <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <WlSpec label="Day sheets in report" value={`${n(content.sheets.approved)} of ${n(content.sheets.total)}`} mono />
          <WlSpec label="Runs" value={n(t.runCount)} mono />
          <WlSpec label="Footage" value={wlReportQty(t.footage, 1)} mono />
          <WlSpec label="Deepest" value={wlReportQty(t.deepest)} mono />
          <WlSpec label="Crew hours" value={wlHours(t.crewHours)} mono />
          <WlSpec label="Shots loaded / fired" value={`${n(t.shotsLoaded)} / ${n(t.shotsFired)}`} mono />
          <WlSpec label="Loaded − fired" value={n(t.derivedMisfires)} mono />
          <WlSpec label="Plugs left in hole" value={n(t.plugsLeftInHole)} mono />
        </dl>
      </WlPanel>

      {content.sheets.included.length > 0 && (
        <WlPanel eyebrow="Day sheets" title="Days in the report" testId="wl-report-days">
          <ReportTable
            head={['Day', 'Unit', 'Window', 'Hours', 'Standby', 'Travel', 'Runs', 'Footage']}
            rows={content.sheets.included.map((s) => [
              wlDay(s.workDate),
              s.unitNumber || '—',
              wlClockRange(s.startTime, s.endTime),
              wlHours(s.sheetHours),
              wlHours(s.standbyHours),
              wlHours(s.travelHours),
              n(s.runCount),
              wlReportQty(s.footage, 1),
            ])}
          />
        </WlPanel>
      )}

      <WlPanel eyebrow="Runs" title="Runs" testId="wl-report-runs">
        {content.runs.length === 0 ? (
          <p className="px-4 py-6 text-sm text-fieldiq-text">No runs are recorded on an approved day sheet.</p>
        ) : (
          <ReportTable
            testId="wl-report-run"
            minWidth="min-w-[980px]"
            head={['Day / unit / run', 'Kind', 'Line', 'Tool string', 'Rig-up – rig down', 'Max depth', 'Tagged', 'Result', 'Footage']}
            rows={content.runs.map((r) => [
              runRef(r),
              r.runKindLabel ?? r.runKind,
              r.lineNumber || '—',
              r.toolStringName || '—',
              wlClockRange(r.rigUpStart, r.rigDown),
              wlReportQty(r.maxDepth),
              wlReportQty(r.taggedDepth),
              r.resultLabel ?? '—',
              wlReportQty(r.footage, 1),
            ])}
          />
        )}
      </WlPanel>

      {content.perforatedIntervals.length > 0 && (
        <WlPanel eyebrow="Perforating" title="Perforated intervals" testId="wl-report-perfs">
          <ReportTable
            minWidth="min-w-[900px]"
            head={['Run', 'Gun', 'Interval', 'Loaded', 'Fired', 'Loaded − fired', 'Misfires entered', 'Charge', 'Disposition']}
            rows={content.perforatedIntervals.map((g) => [
              runRef(g),
              String(g.gunPosition),
              wlReportInterval(g.top, g.bottom),
              n(g.shotsLoaded),
              n(g.shotsFired),
              n(g.derivedMisfires),
              n(g.misfireCount),
              g.chargeType || '—',
              g.misfireDisposition || '—',
            ])}
          />
        </WlPanel>
      )}

      {(content.plugs.length > 0 || content.plugsLeftInHole.length > 0) && (
        <WlPanel eyebrow="Plugs and valves" title="Plugs left in hole" testId="wl-report-plugs-left">
          {content.plugsLeftInHole.length === 0 ? (
            <p className="px-4 py-4 text-sm text-fieldiq-text">None. Every plug this job set, this job also pulled.</p>
          ) : (
            <ReportTable
              head={['Set on', 'Kind', 'Size', 'Serial', 'Depth', 'Owner']}
              rows={content.plugsLeftInHole.map((p) => [
                runRef(p),
                p.plugKindLabel ?? p.plugKind,
                p.sizeText || '—',
                p.serialNumber || '—',
                wlReportQty(p.depth),
                p.ownerLabel ?? p.owner ?? '—',
              ])}
            />
          )}
          <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted" data-testid="wl-report-plugs-caveat">
            {WL_PLUGS_CAVEAT}
          </p>
        </WlPanel>
      )}

      {content.plugs.length > 0 && (
        <WlPanel eyebrow="Plugs and valves" title="Every plug set or pulled" testId="wl-report-plugs">
          <ReportTable
            head={['Run', 'Action', 'Kind', 'Size', 'Serial', 'Depth', 'Owner']}
            rows={content.plugs.map((p) => [
              runRef(p),
              p.actionLabel ?? p.action,
              p.plugKindLabel ?? p.plugKind,
              p.sizeText || '—',
              p.serialNumber || '—',
              wlReportQty(p.depth),
              p.ownerLabel ?? p.owner ?? '—',
            ])}
          />
        </WlPanel>
      )}

      {content.surveyStops.length > 0 && (
        <WlPanel eyebrow="Survey" title="Survey stops" testId="wl-report-stops">
          <ReportTable
            head={['Run', 'Depth', 'Pressure', 'Temperature', 'Dwell']}
            rows={content.surveyStops.map((s) => [
              runRef(s),
              wlReportQty(s.depth),
              wlReportQty(s.pressure),
              wlReportQty(s.temperature, 1),
              s.dwellMinutes === null ? '—' : `${n(s.dwellMinutes)} min`,
            ])}
          />
        </WlPanel>
      )}

      {content.logIntervals.length > 0 && (
        <WlPanel eyebrow="Logging" title="Log intervals" testId="wl-report-logs">
          <ReportTable
            head={['Run', 'Log', 'Interval', 'Repeat pass']}
            rows={content.logIntervals.map((v) => [runRef(v), v.logType || '—', wlReportInterval(v.top, v.bottom), v.repeatPass ? 'Yes' : 'No'])}
          />
        </WlPanel>
      )}

      {content.sources.length > 0 && (
        <WlPanel eyebrow="Radiation" title="Sources run" testId="wl-report-sources">
          <ReportTable
            minWidth="min-w-[900px]"
            head={['Run', 'Isotope', 'Serial', 'Out of shield', 'In hole', 'Out of hole', 'In shield', 'Minutes out']}
            rows={content.sources.map((s) => [
              runRef(s),
              s.isotope || '—',
              s.serialNumber || '—',
              s.outOfShield ?? '—',
              s.inHole ?? '—',
              s.outOfHole ?? '—',
              s.inShield ?? '—',
              n(s.minutesOutOfShield),
            ])}
          />
        </WlPanel>
      )}

      {content.crew.length > 0 && (
        <WlPanel eyebrow="People" title="Crew" testId="wl-report-crew">
          <ReportTable
            head={['Name', 'Positions', 'Days', 'Hours', 'Travel']}
            rows={content.crew.map((c) => [c.name || '—', c.positions.length ? c.positions.join(', ') : '—', n(c.days), wlHours(c.hours), wlHours(c.travelHours)])}
          />
        </WlPanel>
      )}

      {content.units.length > 0 && (
        <WlPanel eyebrow="Units" title="Units" testId="wl-report-units">
          <ReportTable
            head={['Unit', 'Days', 'Sheet hours', 'Runs', 'Footage']}
            rows={content.units.map((u) => [u.unitNumber || '—', n(u.days), wlHours(u.sheetHours), n(u.runCount), wlReportQty(u.footage, 1)])}
          />
        </WlPanel>
      )}

      {content.equipment.length > 0 && (
        <WlPanel eyebrow="Equipment" title="Equipment and rent-ins" testId="wl-report-equipment">
          <ReportTable
            head={['Equipment', 'Unit no.', 'Owned / rented', 'Days', 'Hours', 'Mileage']}
            rows={content.equipment.map((e) => [
              e.name || '—',
              e.unitNumber || '—',
              e.kind === 'owned' ? 'Owned' : e.kind === 'rent_in' ? `Rented${e.vendorName ? ` · ${e.vendorName}` : ''}` : '—',
              n(e.days),
              wlHours(e.hours),
              wlReportQty(e.mileage, 1),
            ])}
          />
        </WlPanel>
      )}

      {content.pressureTests.length > 0 && (
        <WlPanel eyebrow="Pressure control" title="Pressure tests on the day sheets" testId="wl-report-tests">
          <ReportTable
            head={['Day', 'Time', 'Test', 'Result', 'Stack']}
            rows={content.pressureTests.map((p) => [
              wlDay(p.workDate),
              formatInstantTime(p.testedAt, zone) ?? '—',
              pcTestKindLabel(p.testKind),
              (p.result && PC_RESULT_LABEL[p.result]) || '—',
              p.stackName || '—',
            ])}
          />
        </WlPanel>
      )}
    </div>
  );
}
